import type React from "react"
import { Link, usePage } from "@inertiajs/react"
import { Mail, Menu, Phone, TreePine } from "lucide-react"
import logoSrc from "@/images/fclogo.png"
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { useGoogleAnalytics } from "@/lib/analytics"

type SharedProps = {
  routes: Record<string, string | undefined>
  /** Null unless GA_MEASUREMENT_ID is set — see useGoogleAnalytics. */
  analytics?: { measurementId: string } | null
}

const PHONE_DISPLAY = "(570) 245-6393"
const PHONE_HREF = "tel:+15702456393"
const EMAIL = "office@fishingcreektrans.com"
const FACEBOOK_URL = "https://www.facebook.com/FishingCreekTransportation"

const DEPOTS = [
  { label: "Headquarters", street: "2481 State Route 487", cityStateZip: "Orangeville, PA 17859" },
  { label: "Milton", street: "8911 River Road", cityStateZip: "Milton, PA 17856" },
  { label: "Danville", street: "1966 Montour Boulevard", cityStateZip: "Danville, PA 17821" }
]

type Props = { children: React.ReactNode }

export function MarketingLayout({ children }: Props) {
  // Deliberately here and not in application.html.erb: this layout *is* the
  // public site, so the app's authenticated pages can't pick the tag up.
  useGoogleAnalytics(usePage<SharedProps>().props.analytics?.measurementId)

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  )
}

function Header() {
  const { routes } = usePage<SharedProps>().props
  const r = (key: string) => routes[key] ?? "#"

  const navLinks: { label: string; href: string }[] = [
    { label: "Home", href: r("home") },
    { label: "About", href: r("about") },
    { label: "Jobs", href: r("jobs") },
    { label: "Insights", href: r("insights") },
    { label: "Contact", href: r("contact") }
  ]

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/80">
      <div className="container-app flex h-16 items-center justify-between gap-6">
        <Link href={r("home")} className="flex items-center gap-2">
          <Wordmark />
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={PHONE_HREF}
            className="hidden xl:inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {PHONE_DISPLAY}
          </a>
          <Link
            href={r("apply")}
            className="inline-flex items-center justify-center rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-xs hover:opacity-95 transition"
          >
            Apply now
          </Link>
        </div>

        <Sheet>
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="Open menu"
              className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-foreground hover:bg-accent"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="lg:hidden flex flex-col">
            <SheetTitle className="sr-only">Navigation menu</SheetTitle>
            <nav aria-label="Mobile" className="mt-6 flex flex-col gap-1">
              {navLinks.map((link) => (
                <SheetClose asChild key={link.label}>
                  <Link
                    href={link.href}
                    className="rounded-md px-3 py-3 text-base font-medium text-foreground/90 hover:bg-accent"
                  >
                    {link.label}
                  </Link>
                </SheetClose>
              ))}
              <div className="mt-2 grid gap-2">
                <a
                  href={PHONE_HREF}
                  className="inline-flex items-center justify-center gap-2 rounded border border-input bg-background px-4 py-3 text-base font-medium"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  {PHONE_DISPLAY}
                </a>
                <SheetClose asChild>
                  <Link
                    href={r("apply")}
                    className="inline-flex items-center justify-center rounded bg-primary px-4 py-3 text-base font-semibold text-primary-foreground"
                  >
                    Apply now
                  </Link>
                </SheetClose>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}

function Footer() {
  const { routes } = usePage<SharedProps>().props
  const r = (key: string) => routes[key] ?? "#"

  return (
    <footer className="mt-16 border-t border-border bg-muted/40">
      <div className="container-app py-12 grid gap-10 md:grid-cols-4">
        <div className="space-y-3">
          <Wordmark className="h-12" />
          <p className="text-sm text-muted-foreground max-w-[28ch]">
            Safe and reliable school bus transportation in Central
            Pennsylvania, since 1959.
          </p>
          <div className="flex items-center gap-3">
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="text-muted-foreground hover:text-foreground"
            >
              <FacebookGlyph className="h-5 w-5" />
            </a>
            <a
              href={`mailto:${EMAIL}`}
              aria-label="Email FishingCreek"
              className="text-muted-foreground hover:text-foreground"
            >
              <Mail className="h-5 w-5" />
            </a>
            <a
              href={PHONE_HREF}
              aria-label="Call FishingCreek"
              className="text-muted-foreground hover:text-foreground"
            >
              <Phone className="h-5 w-5" />
            </a>
          </div>
        </div>

        <div className="space-y-3">
          <FooterHeading>Locations</FooterHeading>
          <ul className="space-y-3 text-sm">
            {DEPOTS.map((d) => (
              <li key={d.street} className="not-italic">
                <p className="font-medium text-foreground">{d.label}</p>
                <address className="not-italic text-muted-foreground">
                  {d.street}
                  <br />
                  {d.cityStateZip}
                </address>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-3">
          <FooterHeading>Company</FooterHeading>
          <ul className="space-y-2 text-sm">
            <FooterLink href={r("about")}>About</FooterLink>
            <FooterLink href={r("jobs")}>Join our team</FooterLink>
            <FooterLink href={r("insights")}>Insights</FooterLink>
            <FooterLink href={r("contact")}>Contact</FooterLink>
            <FooterLink href={r("apply")}>Apply</FooterLink>
          </ul>
        </div>

        <div className="space-y-3">
          <FooterHeading>Drivers</FooterHeading>
          <ul className="space-y-2 text-sm">
            <FooterLink href={r("training_videos")}>Training videos</FooterLink>
            <FooterLink href="/driver-resources">Driver resources</FooterLink>
            <li>
              <a href={r("login")} className="text-muted-foreground hover:text-foreground">
                Employee login
              </a>
            </li>
          </ul>
          {/* The same tree as the one in the copyright bar below, promoted to
              where drivers actually look. Both point at Digital Debbie on
              purpose: the bar is the habit Joey and Fred trained everyone on,
              this is for the driver who was never told. Label in
              text-foreground rather than text-success — the green passes
              contrast as an icon, not as body text. */}
          <Link
            href={r("digital_debbie")}
            className="mt-2 inline-flex items-center gap-2 rounded-full border border-success/40 bg-success/10 px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:border-success/60 hover:bg-success/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <TreePine className="h-5 w-5 text-success" aria-hidden="true" />
            Driver instructions
          </Link>
          <p className="pt-2 text-sm text-muted-foreground">
            <a href={PHONE_HREF} className="hover:text-foreground">{PHONE_DISPLAY}</a>
            <br />
            <a href={`mailto:${EMAIL}`} className="hover:text-foreground">{EMAIL}</a>
          </p>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="container-app py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} FishingCreek Transportation. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <span>Orangeville, Pennsylvania</span>
            {/* The tree is a long-standing FishingCreek habit: a small green
                marker in the footer that staff know leads to the driver
                instruction sheet. Kept here at its old spot for the people who
                already reach for it, at full strength rather than 70% — the
                labelled chip in the Drivers column above is the discoverable
                one now, so this no longer has to be findable on its own. It's a
                real link, so it carries a real name for anyone on a screen
                reader. */}
            <Link
              href={r("digital_debbie")}
              aria-label="Driver instructions"
              title="Driver instructions"
              className="text-success hover:text-success/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
            >
              <TreePine className="h-5 w-5" aria-hidden="true" />
            </Link>
            <a
              href={r("login")}
              className="font-medium text-foreground hover:text-primary transition-colors"
            >
              Sign in
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

// Lucide's icon set treats brand marks separately; we inline a single-color
// Facebook glyph for the footer rather than depend on a brand-icons package.
function FacebookGlyph({ className }: { className?: string }) {
  return (
    <svg
      role="img"
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z" />
    </svg>
  )
}

function Wordmark({ className = "h-10 md:h-11" }: { className?: string }) {
  return (
    <img
      src={logoSrc}
      alt="FishingCreek Transportation"
      className={`${className} w-auto`}
      loading="eager"
      decoding="async"
    />
  )
}

function FooterHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-foreground">
      {children}
    </p>
  )
}

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <li>
      <Link href={href} className="text-muted-foreground hover:text-foreground">
        {children}
      </Link>
    </li>
  )
}
